import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import star from '../assets/star.png';
import './ShopBase.css';

function ShopBase({ user, other, theme }) {
  const [data, setData] = useState({ rewards: [], chores: [], points: 0 });
  const [modal, setModal] = useState({ open: false, mode: 'confirm', message: '', action: null, item: null });
  const [form, setForm] = useState({ desc: '', cost: '' });
  const [openMenu, setOpenMenu] = useState(null);
  const menuRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const navigate = useNavigate();

  // =========================
  // Fetch all data
  // =========================
  const fetchAll = async () => {
    try {
      const [rewardsRes, choresRes, pointsRes] = await Promise.all([
        fetch(`${API_URL}/users/${user}/rewards`),
        fetch(`${API_URL}/users/${user}/chores`),
        fetch(`${API_URL}/users/${other}/points`),
      ]);
      const [rewardsData, choresData, pointsData] = await Promise.all([
        rewardsRes.json(),
        choresRes.json(),
        pointsRes.json(),
      ]);
      setData({
        rewards: rewardsData.rewards || [],
        chores: choresData.chores || [],
        points: pointsData.points || 0,
      });
    } catch (err) { console.error('Failed to fetch data', err); }
  };

  useEffect(() => { fetchAll(); }, [user, other]);

  // =========================
  // Reward & Chore handlers
  // =========================
  const onRewardClick = (reward) => {
    setModal({ open: true, mode: 'confirm', message: `Redeem "${reward.rewardDesc}"?`, action: 'redeem', item: reward });
  };

  const onChoreClick = (chore) => {
    setModal({ open: true, mode: 'confirm', message: `Complete "${chore.choreDesc}"?`, action: 'complete', item: chore });
  };

  // =========================
  // Confirm handler
  // =========================
// =========================
// Confirm handler
// =========================
  const onConfirm = () => {
    if (modal.action === 'redeem') redeemReward();
    else if (modal.action === 'complete') completeChore();
    else if (
      modal.action === 'add-reward' ||
      modal.action === 'add-chore' ||
      modal.action === 'update-reward' ||
      modal.action === 'update-chore'
    ) {
      saveItem();
    }
  };


  // =========================
  // API actions
  // =========================
  const redeemReward = async () => {
    try {
      const res = await fetch(`${API_URL}/rewards/${user}/${other}/${modal.item.rewardId}/redeem`, { method: 'POST' });
      if (res.status === 400) { setModal({ open: true, mode: 'error', message: 'Not enough points!' }); return; }
      if (!res.ok) throw new Error();
      setModal({ open: true, mode: 'success', message: 'Reward redeemed!' });
      fetchAll();
    } catch { setModal({ open: true, mode: 'error', message: 'Something went wrong' }); }
  };

  const completeChore = async () => {
    try {
      const res = await fetch(`${API_URL}/chores/${user}/${other}/${modal.item.choreId}/complete`, { method: 'POST' });
      if (!res.ok) throw new Error();
      setModal({ open: true, mode: 'success', message: 'Chore completed!' });
      fetchAll();
    } catch { setModal({ open: true, mode: 'error', message: 'Something went wrong' }); }
  };

  const saveItem = async () => {
    try {
      let endpoint = '';

      if (modal.action === 'add-reward' || modal.action === 'update-reward') {
        if (modal.item) {
          // Update reward
          endpoint = `${API_URL}/users/${user}/rewards/${modal.item.id}?desc=${encodeURIComponent(form.desc)}&cost=${form.cost}`;
        } else {
          // Add reward
          endpoint = `${API_URL}/users/${user}/rewards?desc=${encodeURIComponent(form.desc)}&cost=${form.cost}`;
        }
      } else if (modal.action === 'add-chore' || modal.action === 'update-chore') {
        if (modal.item) {
          // Update chore
          endpoint = `${API_URL}/users/${user}/chores/${modal.item.id}?desc=${encodeURIComponent(form.desc)}&cost=${form.cost}`;
        } else {
          // Add chore
          endpoint = `${API_URL}/users/${user}/chores?desc=${encodeURIComponent(form.desc)}&cost=${form.cost}`;
        }
      }

      const res = await fetch(endpoint, { method: 'POST' });
      if (!res.ok) throw new Error();
      setModal({ open: true, mode: 'success', message: 'Saved successfully!' });
      setForm({ desc: '', cost: '' });
      fetchAll();
    } catch {
      setModal({ open: true, mode: 'error', message: 'Failed to save item' });
    }
  };


  const deleteItem = async (type, id) => {
    try {
      const endpoint =
        type === 'chore'
          ? `${API_URL}/users/${user}/chores/${id}`
          : `${API_URL}/users/${user}/rewards/${id}`;

      const res = await fetch(endpoint, { method: 'DELETE' });
      if (!res.ok) throw new Error();

      fetchAll();
      setOpenMenu(null);
    } catch {
      alert('Failed to delete');
    }
  };


  const updateItem = (type, item) => {
    const isReward = type === 'reward';

    setForm({
      desc: isReward ? item.rewardDesc : item.choreDesc,
      cost: isReward ? item.rewardCost : item.choreCost,
    });

    setModal({
      open: true,
      mode: 'edit',
      action: isReward ? 'update-reward' : 'update-chore',
      item: {
        id: isReward ? item.rewardId : item.choreId,
        owner: item.shopOwner,
      },
    });

    setOpenMenu(null);
  };


  // =========================
  // Click outside 3-dot menu
  // =========================
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // =========================
  // Render
  // =========================
  return (
    <div className="shop-base" style={{ backgroundImage: `url(${theme.background})` }}>
      {/* Title bar */}
      <div className="shop-title-bar">
        <button className="btn back-btn" onClick={() => navigate(-1)}>Back</button>
        <button className="btn ok" onClick={() => navigate(`/redeemed-rewards/${user}`)}>Redeemed Rewards</button>
        <h1 className="shop-title">{user.charAt(0).toUpperCase() + user.slice(1)}’s Shop</h1>
        <div className="right-controls">
          <button className="btn ok" onClick={() => navigate(`/completed-chores/${user}`)}>Completed Chores</button>
          <div className="points">
            <img src={star} alt="star" className="points-star" />
            <span className="points-value">{data.points}</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="shop-content">
        {/* Rewards */}
        <div className="panel rewards" style={{ background: theme.rewardsBg, borderColor: theme.rewardsBorder }}>
          <h2 className="panel-title" style={{ color: theme.rewardsTitle }}>
            Rewards
            <button className="btn add-btn" onClick={() => setModal({ open: true, mode: 'add', action: 'add-reward', message: 'Add Reward' })}>+</button>
          </h2>
          <ul>
            {data.rewards.length === 0 && <li className="empty">No rewards</li>}
            {data.rewards.map((r) => (
              <li key={r.rewardId} className="clickable" onClick={() => onRewardClick(r)} style={{ position: 'relative' }}>
                <span className="item-text">{r.rewardDesc}</span>
                <span className="item-cost">-{r.rewardCost}</span>
                <div className="three-dot" ref={openMenu?.type === 'reward' && openMenu.id === r.rewardId ? menuRef : null}
                  onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu?.type === 'reward' && openMenu.id === r.rewardId ? null : { type: 'reward', id: r.rewardId }); }}>
                  ⋮
                  {openMenu?.type === 'reward' && openMenu.id === r.rewardId && (
                    <div className="three-dot-menu" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => updateItem('reward', r)}>Update</button>
                      <button onClick={() => deleteItem('reward', r.rewardId)}>Delete</button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="divider" />

        {/* Chores */}
        <div className="panel chores" style={{ background: theme.choresBg, borderColor: theme.choresBorder }}>
          <h2 className="panel-title" style={{ color: theme.choresTitle }}>
            Chores
            <button className="btn add-btn" onClick={() => setModal({ open: true, mode: 'add', action: 'add-chore', message: 'Add Chore' })}>+</button>
          </h2>
          <ul>
            {data.chores.length === 0 && <li className="empty">No chores</li>}
            {data.chores.map((c) => (
              <li key={c.choreId} className="clickable" onClick={() => onChoreClick(c)} style={{ position: 'relative' }}>
                <span className="item-text">{c.choreDesc}</span>
                <span className="item-cost">+{c.choreCost}</span>
                <div className="three-dot" ref={openMenu?.type === 'chore' && openMenu.id === c.choreId ? menuRef : null}
                  onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu?.type === 'chore' && openMenu.id === c.choreId ? null : { type: 'chore', id: c.choreId }); }}>
                  ⋮
                  {openMenu?.type === 'chore' && openMenu.id === c.choreId && (
                    <div className="three-dot-menu" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => updateItem('chore', c)}>Update</button>
                      <button onClick={() => deleteItem('chore', c.choreId)}>Delete</button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

    {/* Modal */}
    {modal.open && (
      <div className="modal-overlay">
        <div className={`modal ${modal.mode}`}>
          <div>{modal.message}</div>

          {/* Show input fields for both add and edit */}
          {(modal.mode === 'add' || modal.mode === 'edit') && (
            <>
              <input
                placeholder="Description"
                value={form.desc}
                onChange={(e) => setForm({ ...form, desc: e.target.value })}
              />
              <input
                placeholder="Cost"
                type="number"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
              />
            </>
          )}

          {/* Modal buttons */}
          <div className="modal-buttons">
            {modal.mode === 'confirm' || modal.mode === 'add' || modal.mode === 'edit' ? (
              <>
                <button className="btn confirm-yes" onClick={onConfirm}>Yes</button>
                <button className="btn confirm-no" onClick={() => setModal({ ...modal, open: false })}>No</button>
              </>
            ) : (
              <button className="btn ok" onClick={() => setModal({ ...modal, open: false })}>OK</button>
            )}
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

export default ShopBase;
